/*
 * The browser's step — the piece `agent-loop.ts` was missing.
 *
 * That file wires the shared loop to the browser's transport and then takes a `StepFn` from its
 * caller. Nothing constructed one, so the browser could claim a tree, hold a lease, reconcile tool
 * calls, and never call a model. This is that function, and it is deliberately thin: everything that
 * is not a browser fact lives in `@vitesse/agent-loop`.
 *
 * The three facts that decide its shape are all the platform's, not the browser's:
 *
 *   1. THE PROVIDER KEY IS NEVER HERE. The adapter points at our own edge, which injects the key
 *      server-side. This tab has no credential to leak and no upstream host to be redirected to.
 *   2. MONEY AND METERING LIVE AT THE EDGE — it reserves before the call and settles from usage IT
 *      observed, because the browser is the party being metered and a number it supplies is a number
 *      it can choose. Nothing here reports cost.
 *   3. THE CONVERSATION ID TRAVELS, as the provider-affinity key.
 */
import { makeAgentStep, outsideSubset } from '@vitesse/agent-loop';
/**
 * Build the step this tab drives runs with.
 *
 * The model comes from the RUN, not from here — a tree can mix models per run, and the step is told
 * which on every call.
 */
/**
 * The tools one run may use: the tab's union, narrowed to its expert's subset.
 *
 * A run with NO expert is a plain agent and gets everything — narrowing it to nothing would break
 * every un-specialised sub-run. An expert we do not recognise (a roster that changed under a
 * running tree) is treated the same way, because the alternative is a run that silently loses its
 * tools mid-flight for a reason nobody can see.
 *
 * An expert with an EMPTY subset genuinely means no tools, and is not the same as no expert.
 */
export function scopeToExpert(all, expertId, roster) {
    if (!expertId)
        return all;
    const expert = roster.find((e) => e.id === expertId);
    if (!expert)
        return all;
    const want = new Set(expert.toolSubset);
    return all.filter((t) => {
        const name = t?.function?.name;
        return typeof name === 'string' && want.has(name);
    });
}
/*
 * The refusal now lives in `@vitesse/agent-loop`, beside the `ToolOutcome` it returns — the one
 * package both lanes already depend on. It was written out here AND in the worker's server lane,
 * which is how the two come to word the same refusal differently.
 *
 * Re-exported rather than merely imported: this module is where the browser's door lives, so a
 * reader following `gateToExpert` finds the sentence it answers with in the same place.
 */
export { outsideSubset };
/**
 * Hold a dispatcher to the subset its expert declares.
 *
 * ASKS THE SUBSET, NOT THE OFFER. The obvious implementation — narrow the union with
 * `scopeToExpert` and refuse anything not in the result — is wrong twice over, and both ways were
 * caught by existing tests rather than reasoning. It refuses a tool that is not in the union at all
 * (an MCP tool adopted mid-run, a caller whose union is a stub) though no subset excludes it; and it
 * depends on the union's declaration SHAPE, so a caller passing plain `{ name }` had every call
 * refused. The subset is a list of names, so the door reads names.
 *
 * The two escapes match `scopeToExpert`'s exactly — no expert is a plain agent, and an expert the
 * roster has lost keeps its reach rather than losing it mid-flight for a reason nothing reports.
 * Restated here rather than delegated, because the offer narrows DECLARATIONS and the door judges a
 * NAME: one shared helper over two different inputs is how they would come to disagree.
 */
export function gateToExpert(call, expertId, roster) {
    if (!expertId)
        return true;
    const expert = roster.find((e) => e.id === expertId);
    if (!expert)
        return true;
    return expert.toolSubset.includes(call.name);
}
/** `gateToExpert` applied to a dispatcher, for a run whose expert is known. */
function wrapDispatch(dispatch, run, experts) {
    return async (call, ctx) => gateToExpert(call, run.expertId ?? '', experts?.() ?? []) ? dispatch(call, ctx) : outsideSubset(call.name);
}
export function makeBrowserStep({ io, aiUrl, token, platform, tools, dispatch, experts, onPartial, defaultModel = 'openrouter/auto', }) {
    return async (run, ctx) => {
        const provider = 'openrouter';
        // The provider stack, loaded when a step actually runs — see `@vitesse/agent-loop/chat`.
        const { edgeAdapter, tanstackChat } = await import('@vitesse/agent-loop/chat');
        const adapter = edgeAdapter({
            provider,
            baseUrl: aiUrl,
            model: run.model || defaultModel,
            token,
            conversationId: run.conversationId,
            // The surface that started the tree. A spawned agent is attributable to the app it came from,
            // like every other call.
            platform,
            // The run's own identity, on every call it makes. This is what lets the trail show a spawned
            // agent as ONE entry under the run that asked for it, rather than as loose model calls that
            // happen to share a conversation.
            runId: () => run.runId,
            parentRunId: () => run.parentRunId || undefined,
            expertId: run.expertId || undefined,
            // An agent run, not a chat turn — including the ROOT of a tree, which has no parent and
            // would otherwise be indistinguishable from someone typing.
            trigger: 'spawn',
        });
        /*
         * THE STREAMED TEXT IS CLEARED THE MOMENT THE STEP LANDS.
         *
         * The step has appended its durable message, so the viewer should be reading THAT. Leaving the
         * streamed copy up would draw the answer twice — once as the record, once as the ghost of it
         * being written — and a run that finished would keep a half-sentence under its conclusion for
         * as long as the tab was open.
         *
         * In a `finally`, because a step that threw has no durable message either, and its half-written
         * text is not an answer.
         */
        try {
            return await makeAgentStep({
                chat: tanstackChat({ adapter, provider }),
                io,
                defaultModel,
                // Per RUN, not per tree: one tree can hold runs belonging to different experts.
                tools: tools && ((r) => scopeToExpert(tools(), r.expertId ?? '', experts?.() ?? [])),
                /*
                 * AND THE DOOR IS NARROWED THE SAME WAY THE OFFER IS.
                 *
                 * The line above decides what a run is OFFERED; this one decides what it may actually RUN.
                 * Only the offer was scoped, so a model that named a tool it had not been given still had it
                 * executed — the browser's half of the rule `serverstep.ts` enforces on the server side, and
                 * the half that was missing. Observed: a widget author calling `ai_admin_write_widget`, a
                 * tool no expert on this platform declares (0101 removed it from both), and writing two
                 * corrupted widgets to the org shelf with it.
                 *
                 * Derived from `scopeToExpert` rather than from a second list, for the reason the server
                 * gives for doing the same: what is offered and what is accepted cannot be allowed to drift.
                 * `run` is in scope from the step above, so the run's own expert decides — a tree holding
                 * runs of different experts gates each of them by its own subset.
                 */
                dispatch: dispatch && wrapDispatch(dispatch, run, experts),
                systemExtra: (r) => rosterBlock(r.expertId ?? '', experts?.() ?? []),
                // The answer as it is written. This tab is driving the run, so the text exists HERE, in
                // this process, for the whole model call — while every viewer of it was reading the durable
                // archive, which does not receive the message until the call returns.
                ...(onPartial ? { onPartial: (live, r) => onPartial(r.runId, live) } : {}),
            })(run, ctx);
        }
        finally {
            onPartial?.(run.runId, { text: '', reasoning: '', tools: [], parts: [] });
        }
    };
}
/**
 * TELL THE MODEL WHO THE SPECIALISTS ARE, or it will not delegate to them.
 *
 * Observed live: asked to author a statechart, a general flash-tier model reasoned "I don't have
 * documentation of the exact API", guessed, and retried the same invalid SCXML four times against
 * a model-runner that refused it each time — with an `scxml-author` expert sitting in the roster
 * that carries exactly the mechanics it was missing. The spawn tool's own description says to
 * leave `expert_id` empty "unless you have a specific reason", and nothing anywhere gave it one:
 * the roster reached the browser on the handshake and stopped there.
 *
 * So the roster goes into the system text as one short block: the id and the tools each expert
 * holds, which is the part that is not IP and the part a model routes on. Only for a run that is
 * NOT itself an expert — a specialist does not need to be told about itself, and a subagent that
 * re-delegates to its own kind is a loop. Nothing is added when the roster is empty, so a surface
 * with no experts pays no tokens.
 *
 * Cached as a block on its own: it changes when someone edits the roster, not per run.
 */
export function rosterBlock(expertId, roster) {
    if (expertId || roster.length === 0)
        return [];
    const lines = roster.map((e) => `- ${e.id}${e.toolSubset.length ? ` — tools: ${e.toolSubset.join(', ')}` : ''}`);
    return [
        {
            cache: true,
            content: 'Specialists you can delegate to with ai_admin_spawn_subagent by setting expert_id. Each carries instructions and conventions you do not have; when a task is squarely one of theirs, hand it over rather than guessing at it:\n' +
                lines.join('\n'),
        },
    ];
}
//# sourceMappingURL=step.js.map